import React, { useState, useEffect } from "react";
import "./users.css";
import {
  Table,
  Tag,
  Avatar,
  Tabs,
  Popconfirm,
  message,
  ConfigProvider,
  Image,
  Checkbox,
  Modal,
  Spin,
  Empty,
} from "antd";
import { DeleteOutlined, EyeOutlined, UserOutlined } from "@ant-design/icons";

import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import dayjs from "dayjs";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [activeRole, setActiveRole] = useState("vendor");
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [vendorDetailLoading, setVendorDetailLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const hiddenVendorKeys = [
    "_id",
    "id",
    "updatedAt",
    "deviceToken",
    "storeTiming",
    "deliverySetting",
    "socialLinks",
  ];

  const formatLabel = (label = "") =>
    label
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (char) => char.toUpperCase());

  const formatValue = (value, fallback = "N/A") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const isHiddenKey = (key = "") => hiddenVendorKeys.includes(key);

  const getFilteredEntries = (value) =>
    Object.entries(value || {}).filter(([key]) => !isHiddenKey(key));

  const getVendorProfile = (vendorData) =>
    vendorData?.vendor || vendorData || {};

  const renderImageCard = (label, src) => (
    <div className="vendor-image-card" key={label}>
      <div className="vendor-image-title">{label}</div>
      {src ? (
        <Image
          src={src}
          alt={label}
          className={`vendor-modal-image ${label === "Avatar" ? "vendor-avatar-image" : ""}`}
          fallback="https://placehold.co/240x180?text=No+Image"
        />
      ) : (
        <div className="vendor-image-placeholder">No image available</div>
      )}
    </div>
  );

  const getAllUser = (role) => {
    const endpoint =
      role === "vendor" ? routes.getAdminVendors : routes.getAdminCustomers;

    let getRes = (res) => {
      setUsers(
        role === "vendor"
          ? res?.data?.vendors
          : res?.data?.customers || res?.data || [],
      );
    };

    callApi(
      "GET",
      `${endpoint}?page=1&limit=50`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        console.log("error", error);
      },
    );
  };
  const deleteUser = (id) => {
    let getRes = (res) => {
      console.log("res of delete user", res);
      getAllUser(activeRole);
    };

    callApi(
      "DELETE",
      `${routes.deleteUser || routes.getAllUsers}/${id}`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        console.log("error", error);
      },
    );
  };

  const handleApprovalChange = (record, checked) => {
    let getRes = (res) => {
      console.log("res of active status update", res);
      getAllUser(activeRole);
    };

    callApi(
      "PATCH",
      `${routes.setUserActiveStatus}/${record._id}/active-status`,
      { isActive: checked },
      setIsLoading,
      getRes,
      () => message.error("Failed to update active status"),
    );

    // Update local state for immediate UI feedback
    const updatedUsers = users.map((user) =>
      user._id === record._id ? { ...user, isActive: checked } : user,
    );
    setUsers(updatedUsers);
  };

  const fetchVendorDetails = (vendorId) => {
    setIsVendorModalOpen(true);
    setSelectedVendor(null);

    callApi(
      "GET",
      `${routes.getAdminVendorById}/${vendorId}`,
      null,
      setVendorDetailLoading,
      (res) => {
        setSelectedVendor(res?.data || res || null);
      },
      (error) => {
        console.log("error fetching vendor details", error);
        message.error("Failed to fetch vendor details");
      },
    );
  };

  const renderDetailValue = (value, parentKey = "root") => {
    if (value === null || value === undefined || value === "") {
      return <span>N/A</span>;
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return <span>{formatValue(value)}</span>;
    }

    if (Array.isArray(value)) {
      if (!value.length) return <span>N/A</span>;

      return (
        <div className="vendor-detail-array">
          {value.map((item, index) => (
            <div className="vendor-detail-card" key={`${parentKey}-${index}`}>
              {typeof item === "object" && item !== null ? (
                getFilteredEntries(item).map(([key, nestedValue]) => (
                  <div
                    className="vendor-detail-row"
                    key={`${parentKey}-${index}-${key}`}
                  >
                    <span className="vendor-detail-label">
                      {formatLabel(key)}
                    </span>
                    <div className="vendor-detail-value">
                      {renderDetailValue(
                        nestedValue,
                        `${parentKey}-${index}-${key}`,
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="vendor-detail-value">{formatValue(item)}</div>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      const entries = getFilteredEntries(value);

      if (!entries.length) return <span>N/A</span>;

      return (
        <div className="vendor-detail-object">
          {entries.map(([key, nestedValue]) => (
            <div className="vendor-detail-row" key={`${parentKey}-${key}`}>
              <span className="vendor-detail-label">{formatLabel(key)}</span>
              <div className="vendor-detail-value">
                {renderDetailValue(nestedValue, `${parentKey}-${key}`)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <span>{formatValue(value)}</span>;
  };

  const renderVendorSections = (vendorData) => {
    if (!vendorData || typeof vendorData !== "object") {
      return <Empty description="No vendor details found" />;
    }

    return getFilteredEntries(vendorData).map(([sectionKey, sectionValue]) => (
      <div className="vendor-section" key={sectionKey}>
        <h3 className="vendor-section-title">{formatLabel(sectionKey)}</h3>
        <div className="vendor-section-body">
          {renderDetailValue(sectionValue, sectionKey)}
        </div>
      </div>
    ));
  };

  const renderVendorHeader = (vendorData) => {
    const vendor = getVendorProfile(vendorData);
    const summaryItems = [
      { label: "Store", value: vendor.storeName },
      { label: "Email", value: vendor.email },
      { label: "Phone", value: vendor.phone },
      { label: "Status", value: vendor.isActive ? "Active" : "Inactive" },
    ].filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== "",
    );

    return (
      <div className="vendor-hero">
        <div className="vendor-hero-top">
          <div>
            <div className="vendor-hero-title">
              {vendor.name || "Vendor Details"}
            </div>
            <div className="vendor-hero-subtitle">
              {vendor.storeName || vendor.businessName || "Store information"}
            </div>
          </div>
          {vendor.role && <Tag color="orange">{vendor.role.toUpperCase()}</Tag>}
        </div>
        <div className="vendor-summary-grid">
          {summaryItems.map((item) => (
            <div className="vendor-summary-card" key={item.label}>
              <div className="vendor-summary-label">{item.label}</div>
              <div className="vendor-summary-value">
                {formatValue(item.value)}
              </div>
            </div>
          ))}
        </div>
        <div className="vendor-media-grid">
          {renderImageCard("Avatar", vendor.avatar)}
          {renderImageCard("ID Verification", vendor.idVerification)}
        </div>
      </div>
    );
  };

  useEffect(() => {
    getAllUser(activeRole);
  }, [activeRole]);
  const columns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="user-info-cell">
          <Avatar src={record.avatar} icon={<UserOutlined />} />
          <span className="user-name">{text || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={role === "vendor" ? "orange" : "blue"}>
          {role?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "success" : "error"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      responsive: ["md"], // Hidden on small mobile devices
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    ...(activeRole === "vendor"
      ? [
          {
            title: "Vendor Details",
            key: "vendorDetails",
            render: (_, record) => (
              <EyeOutlined
                className="detail-action"
                onClick={() => fetchVendorDetails(record._id)}
              />
            ),
          },
        ]
      : []),
    {
      title: "ID Image",
      dataIndex: "idVerification",
      key: "idVerification",
      render: (imgUrl) =>
        imgUrl ? (
          <Image
            src={imgUrl}
            className="id-verification-thumb"
            fallback="https://placehold.co/60x40?text=No+ID"
          />
        ) : (
          <Tag>No ID</Tag>
        ),
    },
    {
      title: "Approved",
      dataIndex: "isAdminApproved",
      key: "isAdminApproved",
      className: "approval-cell",
      render: (checked, record) => (
        <Checkbox
          checked={checked}
          onChange={(e) => handleApprovalChange(record, e.target.checked)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Popconfirm
          title="Delete User"
          description="Are you sure you want to delete this user?"
          onConfirm={() => deleteUser(record._id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <DeleteOutlined className="delete-action" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FFA450",
        },
      }}
    >
      <div className="user-table-container">
        <Tabs
          defaultActiveKey="vendor"
          onChange={(key) => setActiveRole(key)}
          items={[
            { label: "Vendors", key: "vendor" },
            { label: "Customers", key: "customer" },
          ]}
        />
        <Table
          columns={columns}
          dataSource={users}
          loading={isloading}
          rowKey="_id"
          scroll={{ x: "max-content" }} // Crucial for mobile responsiveness
          pagination={{ pageSize: 10 }}
        />
        <Modal
          title="Vendor Details"
          open={isVendorModalOpen}
          onCancel={() => {
            setIsVendorModalOpen(false);
            setSelectedVendor(null);
          }}
          footer={null}
          width={900}
        >
          {vendorDetailLoading ? (
            <div className="vendor-modal-loading">
              <Spin size="large" />
            </div>
          ) : selectedVendor ? (
            <div className="vendor-modal-content">
              {renderVendorHeader(selectedVendor)}
              {renderVendorSections(selectedVendor)}
            </div>
          ) : (
            <Empty description="No vendor details found" />
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Users;
